import type { AddressNFT, AddressTokenBalance } from 'types/api/address';
import type { AdvancedFilterResponse } from 'types/api/advancedFilter';
import type { InternalTransactionsResponse } from 'types/api/internalTransaction';
import type { SearchRedirectResult, SearchResult } from 'types/api/search';
import type { TokensResponse } from 'types/api/tokens';
import type { TokenTransferResponse } from 'types/api/tokenTransfer';
import type { Transaction } from 'types/api/transaction';

import type { ResourceName, ResourcePayload } from 'lib/api/resources';

import {
  filterTransactions,
  isExcludedAddressHash,
  isExcludedAdvancedFilterItem,
  isExcludedInternalTransaction,
  isExcludedSearchResultItem,
  isExcludedTokenTransfer,
  isExcludedTransaction,
} from './utils';

type ListResponse<T> = {
  items: Array<T>;
  next_page_params: unknown;
};

function filterListResponse<T>(
  data: ListResponse<T>,
  predicate: (item: T) => boolean,
): ListResponse<T> {
  return {
    ...data,
    items: data.items.filter((item) => !predicate(item)),
  };
}

const TX_LIST_RESOURCES: Set<ResourceName> = new Set([
  'txs_validated',
  'txs_pending',
  'txs_with_blobs',
  'txs_watchlist',
  'txs_execution_node',
  'block_txs',
  'address_txs',
  'homepage_txs',
  'homepage_txs_watchlist',
  'optimistic_l2_txn_batch_txs',
  'arbitrum_l2_txn_batch_txs',
  'zkevm_l2_txn_batch_txs',
  'zksync_l2_txn_batch_txs',
  'scroll_l2_txn_batch_txs',
]);

const TOKEN_TRANSFER_RESOURCES: Set<ResourceName> = new Set([
  'token_transfers',
  'token_transfers_all',
  'address_token_transfers',
  'tx_token_transfers',
  'token_instance_transfers',
]);

const INTERNAL_TX_RESOURCES: Set<ResourceName> = new Set([
  'internal_txs',
  'tx_internal_txs',
  'address_internal_txs',
]);

export function filterExcludedApiResponse<R extends ResourceName>(
  resource: R,
  data: ResourcePayload<R>,
): ResourcePayload<R> {
  if (TX_LIST_RESOURCES.has(resource)) {
    if (Array.isArray(data)) {
      return filterTransactions(data as Array<Transaction>) as ResourcePayload<R>;
    }

    return filterListResponse(
      data as ListResponse<Transaction>,
      isExcludedTransaction,
    ) as ResourcePayload<R>;
  }

  if (TOKEN_TRANSFER_RESOURCES.has(resource)) {
    return filterListResponse(
      data as TokenTransferResponse,
      isExcludedTokenTransfer,
    ) as ResourcePayload<R>;
  }

  if (INTERNAL_TX_RESOURCES.has(resource)) {
    return filterListResponse(
      data as InternalTransactionsResponse,
      isExcludedInternalTransaction,
    ) as ResourcePayload<R>;
  }

  if (resource === 'advanced_filter') {
    const response = data as AdvancedFilterResponse;
    return {
      ...response,
      items: response.items.filter((item) => !isExcludedAdvancedFilterItem(item)),
    } as ResourcePayload<R>;
  }

  if (resource === 'search' || resource === 'quick_search') {
    const response = data as SearchResult;
    return {
      ...response,
      items: response.items.filter((item) => !isExcludedSearchResultItem(item)),
    } as ResourcePayload<R>;
  }

  if (resource === 'search_check_redirect') {
    const response = data as SearchRedirectResult;
    if (response.redirect && isExcludedAddressHash(response.parameter)) {
      return { redirect: false } as ResourcePayload<R>;
    }
    return data;
  }

  if (resource === 'tokens' || resource === 'tokens_bridged') {
    const response = data as TokensResponse;
    return {
      ...response,
      items: response.items.filter((item) => !isExcludedAddressHash(item.address)),
    } as ResourcePayload<R>;
  }

  if (resource === 'address_tokens') {
    const response = data as { items: Array<AddressTokenBalance> };
    return {
      ...response,
      items: response.items.filter((item) => !isExcludedAddressHash(item.token.address)),
    } as ResourcePayload<R>;
  }

  if (resource === 'address_nfts') {
    const response = data as { items: Array<AddressNFT> };
    return {
      ...response,
      items: response.items.filter((item) => !isExcludedAddressHash(item.token.address)),
    } as ResourcePayload<R>;
  }

  return data;
}
